/**
 * Transición CRT-off — Fase 12b-1 + 12b-2.
 *
 * Dispara la animación "apagar monitor" antes de navegar a otro
 * destino. Se usa en los dos extremos del CRT:
 *  - sala → CRT: click en el hotspot del monitor (`/login.html`).
 *  - CRT → sala: click en `[ESC] SALIR` o tecla `Escape` desde el menú.
 *
 * El interceptor del lado sala se monta aquí (initShutdownTransition).
 * El lado CRT lo monta el módulo auth, llamando directamente a
 * `triggerShutdown('/')` desde el handler del `.back-link` y desde
 * el keydown de `Escape` cuando ya está en el menú.
 *
 * Persiste un flag en `sessionStorage` antes de navegar para que el
 * boot del destino dispare la animación de power-on inversa. El
 * script inline en `<head>` de `index.html` y `login.html` lee ese
 * flag antes del primer paint y aplica la clase `crt-power-on` al
 * `<html>`. La CSS asociada se carga via `<link>` en ese mismo
 * `<head>` para que esté disponible cuando la clase entra en juego.
 */

/** Path del hotspot del CRT en la sala. */
const CRT_HOTSPOT_HREF = '/login.html';

/**
 * Flags en sessionStorage. 12b-2 los leerá al arrancar el destino.
 *  - WAS_IN_ROOM: "venimos de la sala" → animar power-on del CRT.
 *  - WAS_IN_CRT:  "venimos del CRT" → animar power-on de la sala.
 */
export const TRANSITION_FLAGS = {
  WAS_IN_ROOM: 'crt:wasInRoom',
  WAS_IN_CRT: 'crt:wasInCrt',
} as const;

const ANIM_DURATION_MS = 350;

let inTransition = false;

/**
 * Lanza la animación de apagado y navega al `href` indicado cuando
 * acaba. Si `flagKey` se pasa, marca ese valor en sessionStorage para
 * que el destino sepa que viene de una transición.
 *
 * Respeta `prefers-reduced-motion` — navega directo sin animar.
 * Idempotente: una segunda llamada mientras la transición ya está
 * en marcha se ignora.
 */
export function triggerShutdown(href: string, flagKey?: string): void {
  if (inTransition) return;

  if (flagKey) setFlag(flagKey);

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.location.href = href;
    return;
  }

  inTransition = true;
  document.documentElement.classList.add('crt-shutdown');
  window.setTimeout(() => {
    window.location.href = href;
  }, ANIM_DURATION_MS);
}

/**
 * Instala el interceptor en la sala que dispara la transición al
 * pulsar el hotspot del CRT (monitor). El resto de hotspots de la
 * sala navegan normal — la transición es específica del par
 * sala ↔ CRT por decisión de diseño.
 */
export function initShutdownTransition(): void {
  const wrapper = document.querySelector<HTMLElement>('.scene-wrapper');
  if (!wrapper) return;

  // Bubble phase: respetamos cualquier `preventDefault` previo
  // (lo comprueba el guard `ev.defaultPrevented` de abajo).
  wrapper.addEventListener('click', (ev) => {
    if (ev.defaultPrevented) return;

    const me = ev as MouseEvent;
    if (me.button !== 0) return;
    if (me.ctrlKey || me.metaKey || me.shiftKey || me.altKey) return;

    const a = (ev.target as HTMLElement).closest<HTMLAnchorElement>(
      `a.hotspot[href="${CRT_HOTSPOT_HREF}"]`,
    );
    if (!a) return;
    if (a.target === '_blank') return;

    ev.preventDefault();
    triggerShutdown(CRT_HOTSPOT_HREF, TRANSITION_FLAGS.WAS_IN_ROOM);
  });
}

function setFlag(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage puede fallar en modo privado en ciertos browsers.
    // No es crítico — el destino simplemente no animará la entrada.
  }
}
